// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 — "POT classic" shell: the 48px gradient header, the ☰ 300px navigation drawer and the
// legal footer of the standalone ASP POT app (asp-pot sidebar.js / facelift.css), so a nurse who
// opens /pot in OwlRx sees the chrome she already knows. Spec: asp-pot-screen-spec.md §0.3–0.5.
//
// Role mapping (asp-pot role → OwlRx): nurse = provider_nurse; nurse_manager = provider_nurse with
// carerx.nurses.manage; admin/pharmacy_manager/pharmacist/intake/billing_manager = pharmacy staff
// (role_name 'admin' | 'pharmacy_staff' | 'pharmacist' …). We gate drawer items on OwlRx
// permissions where one exists and on the role bucket otherwise.
import { useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { useModules } from '../../auth/ModulesContext'
import { usePharmacy } from '../../auth/PharmacyContext'
import { POT, potPage, potContent } from './potTheme'
import { PotOverlays } from './overlays'
import { SecurityTraining } from './overlays/SecurityTraining'
import IdleLogoutGuard from '../../shell/IdleLogoutGuard'

export type PotRole = 'admin' | 'pharmacy_manager' | 'pharmacist' | 'intake' | 'billing_manager' | 'nurse_manager' | 'nurse' | 'provider'

/** Derive the asp-pot role the current OwlRx user most closely matches. */
export function usePotRole(): PotRole {
  const { me } = useAuth()
  return potRoleOf(me?.role_name, me?.permissions)
}

/**
 * The asp-pot role for an OwlRx role name + permissions (pure; unit-tested). Owner 2026-10-29: a Technician
 * is the legacy intake role (it fell through to pharmacy_manager and got the manager's sidebar), and a
 * Provider — the prescriber, who had no role in the legacy app — sees only what every role sees.
 */
export function potRoleOf(roleName: string | null | undefined, permissions: readonly string[] | null | undefined): PotRole {
  const role = (roleName ?? '').toLowerCase()
  const perms = new Set(permissions ?? [])
  if (role.includes('nurse')) return perms.has('carerx.nurses.manage') || perms.has('carerx.manage') ? 'nurse_manager' : 'nurse'
  if (role.includes('pharmacist')) return 'pharmacist'
  if (role.includes('billing')) return 'billing_manager'
  if (role.includes('intake') || role.includes('technician')) return 'intake'
  if (role === 'admin' || role.includes('owner') || perms.has('pharmacy.self.manage')) return 'admin'
  if (role === 'provider') return 'provider'
  return 'pharmacy_manager'
}

/** The tenant's display name for the POT chrome and copy (never the PMS vendor name). */
export function usePotPharmacyName(): string {
  const { me } = useAuth()
  const { activePharmacy } = usePharmacy()
  return activePharmacy?.display_name || activePharmacy?.name || me?.pharmacy_slug?.replace(/-/g, ' ') || 'Pharmacy'
}

interface NavItem { icon: string; label: string; to: string; roles: PotRole[] }
interface NavSection { title: string; items: NavItem[] }
const ALL: PotRole[] = ['admin', 'pharmacy_manager', 'pharmacist', 'intake', 'billing_manager', 'nurse_manager', 'nurse', 'provider']

/** Drawer contents, verbatim order from asp-pot sidebar.js (spec §0.4). */
export const POT_NAV: NavSection[] = [
  { title: 'CLINICAL', items: [
    { icon: '📋', label: 'Dashboard', to: '/pot/dashboard', roles: ALL },
    { icon: '📝', label: 'New Assessment', to: '/pot/new', roles: ['admin', 'pharmacy_manager', 'nurse_manager', 'nurse'] },
    { icon: '📥', label: 'PCW Intake', to: '/pot/pcw-intake', roles: ['admin', 'pharmacy_manager', 'intake', 'pharmacist'] },
    { icon: '🧮', label: 'PCW Pharmacist', to: '/pot/pcw', roles: ['admin', 'pharmacy_manager', 'pharmacist'] },
    { icon: '🔄', label: 'Re-Careplanning', to: '/pot/recareplan', roles: ['admin', 'pharmacy_manager', 'pharmacist', 'nurse_manager'] },
    { icon: '🧪', label: 'Lab Tracker', to: '/pot/labs', roles: ['admin', 'pharmacy_manager', 'pharmacist', 'nurse_manager'] },
  ] },
  { title: 'PATIENTS', items: [
    { icon: '👥', label: 'Patients', to: '/pot/patients', roles: ALL },
    { icon: '📊', label: 'Insights', to: '/pot/insights', roles: ALL },
    { icon: '📥', label: 'Enrollments', to: '/pot/enrollments', roles: ['admin', 'pharmacy_manager', 'pharmacist'] },
    { icon: '🩺', label: 'Physicians', to: '/pot/physicians', roles: ['admin', 'pharmacy_manager', 'pharmacist'] },
  ] },
  { title: 'OPERATIONS', items: [
    { icon: '📈', label: 'Analytics', to: '/pot/analytics', roles: ['admin', 'pharmacy_manager', 'nurse_manager', 'billing_manager'] },
    { icon: '💊', label: 'Drug Formulary', to: '/pot/drugs', roles: ['admin', 'pharmacy_manager', 'pharmacist'] },
    { icon: '👥', label: 'Teams', to: '/pot/manager', roles: ['admin', 'nurse_manager'] },
    { icon: '💰', label: 'Billing', to: '/pot/billing', roles: ['admin', 'intake', 'billing_manager'] },
  ] },
  { title: 'ADMINISTRATION', items: [
    { icon: '⚙️', label: 'Settings & Admin', to: '/pot/admin', roles: ['admin'] },
    { icon: '👩‍⚕️', label: 'Users', to: '/pot/nurses', roles: ['admin'] },
    { icon: '📨', label: 'Data Requests', to: '/pot/admin/dsr', roles: ['admin'] },
    { icon: '📬', label: 'Feedback Queue', to: '/pot/admin/feedback', roles: ['admin'] },
    { icon: '📊', label: 'Roadmap', to: '/pot/roadmap', roles: ['admin'] },
  ] },
  { title: 'SYSTEM', items: [
    { icon: '👤', label: 'My Account', to: '/pot/account', roles: ALL },
    { icon: '❓', label: 'Help', to: '/pot/help', roles: ALL },
    { icon: '🔒', label: 'Privacy', to: '/legal/privacy', roles: ALL },
  ] },
]

/** Header page names (spec §0.3 table). */
export function PotShell({ page, children, notifications }: { page: string; children: ReactNode; notifications?: number }) {
  const { me, signOut } = useAuth() as ReturnType<typeof useAuth> & { signOut?: () => Promise<void> | void }
  const role = usePotRole()
  // D-POT-2: a nurse's "My visits" belongs to the native POT experience, which is off whenever the
  // pharmacy runs POT classic (the two are exclusive) — then "Back to OwlRx" is the CareRx page itself.
  const { isDisabled } = useModules()
  const nurseHome = isDisabled('carerx_pot_native') ? '/carerx' : '/carerx/my-visits'
  const backToOwlRx = role === 'nurse' ? nurseHome : '/carerx'
  const backToOwlRxLabel = backToOwlRx === '/carerx/my-visits' ? 'CareRx — My visits' : 'CareRx'
  const [open, setOpen] = useState(false)
  const loc = useLocation()
  const nav = useNavigate()
  const { activePharmacy } = usePharmacy()
  const pharmacyName = usePotPharmacyName()
  const logoUrl = activePharmacy?.logo_url || null
  const initial = (me?.display_name || me?.email || '?').trim().charAt(0).toUpperCase()

  return (
    <div style={potPage}>
      {/* ── .fl-header ── */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, height: 48, background: POT.headerGradient, color: '#fff', display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px' }}>
        <button type="button" aria-label="Open menu" onClick={() => setOpen(true)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', padding: 0, lineHeight: 1 }}>☰</button>
        <div aria-hidden style={{ width: 32, height: 32, borderRadius: 6, background: 'rgba(255,255,255,.95)', display: 'grid', placeItems: 'center', color: POT.blue, fontWeight: 700, fontSize: 14, overflow: 'hidden' }}>{logoUrl ? <img src={logoUrl} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} /> : pharmacyName.charAt(0)}</div>
        <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {pharmacyName} <span style={{ fontWeight: 400, opacity: .7 }}>· {page}</span>
        </div>
        {/* Owner 2026-10-20: the way back. POT classic is a skin inside OwlRx; a nurse returns to her
            CareRx visits, staff/admins to CareRx (a nurse's "/" lands on POT again, so never link "/"). */}
        <Link to={backToOwlRx} style={{ marginLeft: 'auto', color: '#fff', textDecoration: 'none', fontSize: 12, opacity: .85, whiteSpace: 'nowrap', border: '1px solid rgba(255,255,255,.35)', borderRadius: 6, padding: '4px 10px' }}>← Back to OwlRx</Link>
        {notifications !== undefined && (
          <button type="button" aria-label="Notifications" style={{ position: 'relative', background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.3)', color: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>
            🔔{notifications > 0 && <span style={{ position: 'absolute', top: -6, right: -6, background: POT.danger, color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 6px' }}>{notifications}</span>}
          </button>
        )}
      </header>

      {/* ── drawer ── */}
      {open && <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60 }} />}
      <nav aria-label="POT navigation" style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: 300, background: '#fff', zIndex: 61, boxShadow: POT.shadowLg, transform: open ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform .25s', overflowY: 'auto', fontFamily: POT.font }}>
        <div style={{ background: POT.headerGradient, color: '#fff', padding: '18px 16px', display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: '#fff', display: 'grid', placeItems: 'center', color: POT.blue, fontWeight: 700 }}>{pharmacyName.charAt(0)}</div>
          <div><div style={{ fontSize: 13, fontWeight: 600 }}>{pharmacyName}</div><div style={{ fontSize: 10, opacity: .8 }}>Modern Care · Personalized Precision</div></div>
          <button type="button" aria-label="Close menu" onClick={() => setOpen(false)} style={{ position: 'absolute', top: 8, right: 10, background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${POT.border}` }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: POT.headerGradient, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 600 }}>{initial}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{me?.display_name || me?.email}</div>
            <div style={{ fontSize: 11, color: POT.text3 }}>{me?.email}</div>
            <span style={{ background: POT.blueLt, color: POT.blue, fontSize: 10, fontWeight: 600, borderRadius: 10, padding: '1px 8px' }}>{role.replace('_', ' ')}</span>
          </div>
        </div>
        <Link to={backToOwlRx} onClick={() => setOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: `1px solid ${POT.border}`, color: POT.blue, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>← Back to OwlRx<span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 400, color: POT.text3 }}>{backToOwlRxLabel}</span></Link>
        <div style={{ padding: '8px 10px 24px' }}>
          {POT_NAV.map(sec => {
            const items = sec.items.filter(i => i.roles.includes(role))
            if (items.length === 0) return null
            return (
              <div key={sec.title} style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: POT.text3, padding: '4px 12px', letterSpacing: '0.04em' }}>{sec.title}</div>
                {items.map(i => {
                  const active = loc.pathname === i.to
                  return (
                    <Link key={i.label} to={i.to} onClick={() => setOpen(false)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500, textDecoration: 'none', color: active ? POT.blue : POT.text, background: active ? POT.blueLt : 'transparent' }}>
                      <span aria-hidden>{i.icon}</span>{i.label}
                    </Link>
                  )
                })}
              </div>
            )
          })}
          <div style={{ marginTop: 10 }}>
            <button type="button" onClick={async () => { setOpen(false); if (signOut) await signOut(); nav('/login') }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500, border: 'none', background: 'transparent', color: POT.danger, cursor: 'pointer', fontFamily: POT.font }}>
              <span aria-hidden>⏻</span>Sign Out
            </button>
          </div>
        </div>
      </nav>

      <main style={potContent}>{children}</main>
      <PotOverlays />
      {/* asp-pot training gate: acknowledgment required before using the tool (logged + audited). */}
      <SecurityTraining />
      {/* HIPAA automatic logoff: /pot renders outside AppShell, so it mounts the same guard itself. */}
      <IdleLogoutGuard />

      {/* ── legal footer ── */}
      <footer style={{ textAlign: 'center', fontSize: 11, color: POT.text3, padding: '16px 0 24px' }}>
        <Link to="/legal/privacy" style={{ color: POT.text3 }}>Privacy Policy</Link> · <Link to="/legal/terms" style={{ color: POT.text3 }}>Terms of Service</Link> · HIPAA Notice
        <div>© {new Date().getFullYear()} {pharmacyName} · HIPAA &amp; CCPA compliant</div>
      </footer>
    </div>
  )
}
